import { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useMessageInputContext, useChannelActionContext } from 'stream-chat-react';
import { InputFooter } from '@bizzabo/collaboration-common';
import useTypeTracking from '../../analytics';
import { useChatClient, useUserBanListener } from '@bizzabo/chat-core';

const preventClickAwayElements = ['emoji-picker', 'profile-card', 'profile-card-opener'];
const PLACEHOLDER_TEXT = 'Start a new conversation';
const PLACEHOLDER_REPLY_TEXT = 'Reply';
const maxMessageLength = 400;

const CustomMessageInput: React.FC = () => {
    const classes = useStyles();
    const { parent } = useMessageInputContext();
    const [message, setMessage] = useState({ text: '', anonymous: false });
    const [isReactionsOpen, setIsReactionsOpen] = useState(false);
    const { sendMessage } = useChannelActionContext();
    const { client } = useChatClient();
    const { trackSystemEvent } = useTypeTracking();
    const isMyUserBanned = useUserBanListener(client);
    const [inputOpen, setInputOpen] = useState(!!parent && !isMyUserBanned);

    const sendMessageRequest = () => {
        const { text } = message;
        const messageObj = {
            text: text,
            parent: parent,
        };
        return sendMessage(messageObj);
    };

    const handleSendMessage = async () => {
        await sendMessageRequest();

        setMessage({ text: '', anonymous: false });
        setIsReactionsOpen(false);
        setInputOpen(false);

        const description = parent
            ? {
                  parent_type: parent.id && 'TEXT',
                  parent_id: parent.id,
                  is_parent_pinned: parent.pinned,
              }
            : {};

        trackSystemEvent({
            systemName: 'CHAT_MESSAGE_SENT',
            description: description,
        });
    };

    const toggleExpand = (expand: boolean) => {
        if (!isMyUserBanned) {
            setInputOpen(expand);
        }
    };

    const toggleEmojiPicker = (expand: boolean) => {
        setIsReactionsOpen(expand);
    };

    const emptyOpenInput = !inputOpen || !message.text.trim().length;

    const disabledSendMessage = emptyOpenInput || isMyUserBanned;

    return (
        <div className={isMyUserBanned && classes.inputContainer}>
            <InputFooter
                toggleInputExpand={toggleExpand}
                placeholderText={parent ? PLACEHOLDER_REPLY_TEXT : PLACEHOLDER_TEXT}
                message={message}
                setMessage={setMessage}
                isInputOpen={inputOpen}
                enableAnonymousMessage={false}
                preventClickAwayElements={preventClickAwayElements}
                sendMessage={handleSendMessage}
                isSendDisabled={disabledSendMessage}
                displayReactionsButton={true}
                isReactionsOpen={isReactionsOpen}
                toggleReactions={toggleEmojiPicker}
                isEmojiButtonDisabled={!inputOpen}
                maxInputLength={maxMessageLength}
            />
        </div>
    );
};

const useStyles = makeStyles(
    () => ({
        inputContainer: { opacity: '0.4' },
    }),
    {
        classNamePrefix: 'CustomMessageInput',
    }
);

export default CustomMessageInput;
